import { useQueryClient } from '@tanstack/react-query';
import {
  useTasksServiceGetApiTasksMyAssigned,
  useTasksServiceGetApiTasksRecent,
  useTasksServiceGetApiTasksOverdue,
  useTasksServicePutApiTasksById,
  useTasksServiceDeleteApiTasksById,
  useTasksServicePutApiTasksByIdAssignByKeycloakUserId,
  useTasksServicePutApiTasksByIdUnassign
} from '../api/generated/queries/queries';
import { TaskDTO } from '../api/generated/requests/types.gen';
import { useSnackbar } from 'notistack';


/**
 * Hook do pobierania zadań przypisanych do bieżącego użytkownika z cache'owaniem
 * Używa React Query do cache'owania danych
 */
export const useMyTasks = () => {
  return useTasksServiceGetApiTasksMyAssigned([], {
    staleTime: 0, // Zawsze wykonuj request w tle, ale serwuj dane z cache od razu
    gcTime: 30 * 60 * 1000, // 30 minut w cache
    refetchOnWindowFocus: false, // Nie odświeżaj automatycznie przy focus
    refetchOnMount: true, // Zawsze odśwież przy mount komponentu
    enabled: true, // Explicitly enable the query
  });
};

/**
 * Hook do pobierania ostatnich zadań z cache'owaniem
 * Używa React Query do cache'owania danych
 */
export const useRecentTasks = (since?: string) => {
  // Domyślnie pobierz zadania z ostatnich 30 dni
  const defaultSince = since || (() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return thirtyDaysAgo.toISOString();
  })();

  return useTasksServiceGetApiTasksRecent({ since: defaultSince }, [], {
    staleTime: 0, // Zawsze wykonuj request w tle, ale serwuj dane z cache od razu
    gcTime: 30 * 60 * 1000, // 30 minut w cache
    refetchOnWindowFocus: false, // Nie odświeżaj automatycznie przy focus
    refetchOnMount: true, // Zawsze odśwież przy mount komponentu
    enabled: true, // Explicitly enable the query
  });
};

/**
 * Hook do pobierania przeterminowanych zadań z cache'owaniem
 * Używa React Query do cache'owania danych
 */
export const useOverdueTasks = () => {
  return useTasksServiceGetApiTasksOverdue([], {
    staleTime: 0, // Zawsze wykonuj request w tle, ale serwuj dane z cache od razu
    gcTime: 30 * 60 * 1000, // 30 minut w cache
    refetchOnWindowFocus: false, // Nie odświeżaj automatycznie przy focus
    refetchOnMount: true, // Zawsze odśwież przy mount komponentu
    enabled: true, // Explicitly enable the query
  });
};

/**
 * Hook do mutacji zadań z automatyczną aktualizacją cache
 */
export const useTaskMutations = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Funkcja do aktualizacji zadania w cache bez ponownego pobierania
  const updateTaskInCache = (taskId: number, updates: Partial<TaskDTO>) => {
    // Aktualizuj cache dla wszystkich zadań
    queryClient.setQueryData(['TasksServiceGetApiTasks'], (oldData: any) => {
      if (!oldData) return oldData;
      return oldData.map((task: any) => 
        task.id === taskId ? { ...task, ...updates } : task
      );
    });

    // Aktualizuj cache dla moich zadań
    queryClient.setQueryData(['TasksServiceGetApiTasksMyAssigned'], (oldData: any) => {
      if (!oldData) return oldData;
      return oldData.map((task: any) => 
        task.id === taskId ? { ...task, ...updates } : task
      );
    });

    // Aktualizuj cache dla zapytań paginowanych - używanych przez Kanban i TasksPage
    queryClient.setQueriesData(
      { queryKey: ['TasksServiceGetApiTasksPaginated'] },
      (oldData: any) => {
        if (!oldData?.content) return oldData;
        return {
          ...oldData,
          content: oldData.content.map((task: any) => 
            task.id === taskId ? { ...task, ...updates } : task
          )
        };
      }
    );
  };

  // Funkcja do usunięcia zadania z cache
  const removeTaskFromCache = (taskId: number) => {
    // Usuń z cache wszystkich zadań
    queryClient.setQueryData(['TasksServiceGetApiTasks'], (oldData: any) => {
      if (!oldData) return oldData;
      return oldData.filter((task: any) => task.id !== taskId);
    });

    // Usuń z cache moich zadań
    queryClient.setQueryData(['TasksServiceGetApiTasksMyAssigned'], (oldData: any) => {
      if (!oldData) return oldData;
      return oldData.filter((task: any) => task.id !== taskId);
    });

    // Usuń z cache zapytań paginowanych - używanych przez Kanban i TasksPage
    queryClient.setQueriesData(
      { queryKey: ['TasksServiceGetApiTasksPaginated'] },
      (oldData: any) => {
        if (!oldData?.content) return oldData;
        return {
          ...oldData,
          content: oldData.content.filter((task: any) => task.id !== taskId)
        };
      }
    );
  };

  // Mutation do aktualizacji zadania z optymistyczną aktualizacją
  const updateTaskMutation = useTasksServicePutApiTasksById({
    onMutate: async (variables) => {
      // Anuluj wszystkie wychodzące zapytania dla zadań
      await queryClient.cancelQueries({ queryKey: ['TasksServiceGetApiTasks'] });
      await queryClient.cancelQueries({ queryKey: ['TasksServiceGetApiTasksMyAssigned'] });

      // Zapisz poprzednie dane na wypadek rollback
      const previousAllTasks = queryClient.getQueryData(['TasksServiceGetApiTasks']);
      const previousMyTasks = queryClient.getQueryData(['TasksServiceGetApiTasksMyAssigned']);

      // Optymistycznie zaktualizuj cache
      updateTaskInCache(variables.id, variables.requestBody);

      // Zwróć kontekst z poprzednimi danymi
      return { previousAllTasks, previousMyTasks };
    },
    onSuccess: (data, variables) => {
      // Zaktualizuj cache z rzeczywistymi danymi z serwera
      updateTaskInCache(variables.id, data);
      enqueueSnackbar('Zadanie zostało zaktualizowane', { variant: 'success' });
    },
    onError: (error, variables, context) => {
      // Przywróć poprzednie dane w przypadku błędu
      if (context?.previousAllTasks) {
        queryClient.setQueryData(['TasksServiceGetApiTasks'], context.previousAllTasks);
      }
      if (context?.previousMyTasks) {
        queryClient.setQueryData(['TasksServiceGetApiTasksMyAssigned'], context.previousMyTasks);
      }

      console.error('Błąd podczas aktualizacji zadania:', error);
      enqueueSnackbar('Nie udało się zaktualizować zadania', { variant: 'error' });
    },
    onSettled: () => {
      // Zawsze odśwież dane po zakończeniu mutacji (sukces lub błąd)
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasks'] });
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksMyAssigned'] });
      
      // Invalidate paginated queries - używane przez Kanban, TasksPage i dashboard widgets
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksPaginated'] });
      
      // Invalidate other task-related queries that might be affected by status changes
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksRecent'] });
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksOverdue'] });
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksStatusByStatus'] });
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksMyAssignedStatusByStatus'] });
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksEndDateBetween'] });
      
      // Invalidate calendar-specific queries - fix for calendar not updating after event moves/resizes
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksStartDateBetween'] });
    }
  });

  // Mutation do usuwania zadania
  const deleteTaskMutation = useTasksServiceDeleteApiTasksById({
    onSuccess: (data, variables) => {
      removeTaskFromCache(variables.id);
      enqueueSnackbar('Zadanie zostało usunięte', { variant: 'success' });
    },
    onError: (error) => {
      console.error('Błąd podczas usuwania zadania:', error);
      enqueueSnackbar('Nie udało się usunąć zadania', { variant: 'error' });
    }
  });

  // Mutation do przypisywania zadania do użytkownika
  const assignUserMutation = useTasksServicePutApiTasksByIdAssignByKeycloakUserId({
    onSuccess: (data, variables) => {
      updateTaskInCache(variables.id, { 
        assignedToUserId: variables.keycloakUserId,
        updatedAt: data.updatedAt 
      });
      // Odśwież wszystkie zapytania związane z zadaniami
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksMyAssigned'] });
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksPaginated'] });
      enqueueSnackbar('Zadanie zostało przypisane do użytkownika', { variant: 'success' });
    },
    onError: (error) => {
      console.error('Błąd podczas przypisywania zadania do użytkownika:', error);
      enqueueSnackbar('Nie udało się przypisać zadania do użytkownika', { variant: 'error' });
    }
  });

  // Mutation do usuwania przypisania zadania
  const unassignUserMutation = useTasksServicePutApiTasksByIdUnassign({
    onSuccess: (data, variables) => {
      updateTaskInCache(variables.id, { 
        assignedToUserId: undefined,
        updatedAt: data.updatedAt 
      });
      // Odśwież wszystkie zapytania związane z zadaniami
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksMyAssigned'] });
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksPaginated'] });
      enqueueSnackbar('Przypisanie zadania zostało usunięte', { variant: 'success' });
    },
    onError: (error) => {
      console.error('Błąd podczas usuwania przypisania zadania:', error);
      enqueueSnackbar('Nie udało się usunąć przypisania zadania', { variant: 'error' });
    }
  });


  return {
    updateTask: updateTaskMutation.mutateAsync,
    deleteTask: deleteTaskMutation.mutateAsync,
    assignUser: assignUserMutation.mutateAsync,
    unassignUser: unassignUserMutation.mutateAsync,

    // Stany mutacji
    isUpdating: updateTaskMutation.isPending,
    isDeleting: deleteTaskMutation.isPending,
    isAssigning: assignUserMutation.isPending,
    isUnassigning: unassignUserMutation.isPending,
  };
};

/**
 * Hook do zarządzania danymi dashboardu z automatycznym cache'owaniem
 * Pobiera ostatnie i przeterminowane zadania
 */
export const useDashboardTaskData = (since?: string) => {
  const queryClient = useQueryClient();

  // Pobierz dane z cache'owanych hooków
  const recentTasksQuery = useRecentTasks(since);
  const overdueTasksQuery = useOverdueTasks();

  // Mapowanie danych do odpowiedniego formatu (podobnie jak w oryginalnym kodzie)
  const mapTaskData = (task: any): TaskDTO => ({
    id: task.id,
    assignedToUserId: task.assignedToUserId,
    title: task.title || '',
    description: task.description || '',
    status: (task.status as 'TODO' | 'IN_PROGRESS' | 'CANCELED' | 'DONE') || 'TODO',
    priority: task.priority || 'C',
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
    startDate: task.startDate,
    endDate: task.endDate || new Date().toISOString(),
    createdByUserId: task.createdByUserId,
    taskType: task.taskType,
    prefixId: task.prefixId
  });

  // Zmapowane dane
  const recentTasks = (recentTasksQuery.data || []).map(mapTaskData);
  const overdueTasks = (overdueTasksQuery.data || []).map(mapTaskData);

  // Funkcja do odświeżania danych dashboardu
  const refreshDashboardData = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksRecent'] }),
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksOverdue'] })
    ]);
  };

  return {
    // Dane
    recentTasks: recentTasks.slice(0, 5), // Ograniczenie do 5 zadań jak w oryginalnym kodzie
    overdueTasks: overdueTasks.slice(0, 5), // Ograniczenie do 5 zadań jak w oryginalnym kodzie

    // Stany ładowania
    isLoadingRecent: recentTasksQuery.isLoading,
    isLoadingOverdue: overdueTasksQuery.isLoading,
    isLoading: recentTasksQuery.isLoading || overdueTasksQuery.isLoading,

    // Stany błędów
    recentError: recentTasksQuery.error,
    overdueError: overdueTasksQuery.error,

    // Funkcje pomocnicze
    refreshDashboardData,

    // Oryginalne query objects dla zaawansowanego użycia
    recentTasksQuery,
    overdueTasksQuery
  };
};
